/**
 * Liste de produits réordonnable par drag-and-drop (long-press 500ms) — scope
 * par catégorie nav tree. Utilise @dnd-kit (PointerSensor + activationConstraint
 * delay 500 = long-press natif). Optimistic : réordonne en local au drop puis
 * persiste via onReorder ; le parent recharge en cas d'échec.
 */
import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { Badge } from './ui/Badge';

export interface SortableProduct {
  id: string;
  name: string;
  price: number;
  type: 'liquid' | 'solid' | 'other';
  available: boolean;
}
export interface SortableItem {
  product: SortableProduct;
  hidden: boolean; // hérité d'un nœud masqué
}

interface Props {
  items: SortableItem[];
  isClientHidden: (id: string) => boolean;
  admin: boolean;
  onEdit: (p: SortableProduct) => void;
  /** Appelé au drop avec le nouvel ordre des ids (déjà appliqué en local). */
  onReorder: (orderedIds: string[]) => void;
}

function ProductRow({
  item, isClientHidden, admin, onEdit,
}: { item: SortableItem; isClientHidden: (id: string) => boolean; admin: boolean; onEdit: (p: SortableProduct) => void }) {
  const p = item.product;
  const clientHidden = isClientHidden(p.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    // L'item saisi est soulevé (z + ombre via classe), les autres glissent.
    zIndex: isDragging ? 30 : undefined,
    position: isDragging ? 'relative' : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`staff-product-card sortable-card ${isDragging ? 'dragging' : ''} ${!p.available ? 'unavailable' : ''} ${item.hidden || clientHidden ? 'hidden-node' : ''}`}
      {...attributes}
      {...listeners}
    >
      <span className="sortable-grip" aria-hidden="true">⠿</span>
      <div className="staff-product-info">
        <strong>{p.name}</strong>
      </div>
      <div className="staff-product-right">
        {clientHidden && <Badge tone="neutral" size="sm">Masqué client</Badge>}
        {item.hidden && <Badge tone="neutral" size="sm">Masqué</Badge>}
        {!p.available && <Badge tone="long" size="sm">Rupture</Badge>}
        <span className="staff-product-price">{p.price.toFixed(2)} €</span>
        {admin && (
          <button
            type="button"
            className="staff-product-edit"
            aria-label="Éditer la fiche"
            // Empêche le pointerdown de démarrer un drag depuis le bouton (tap = édition).
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onEdit(p)}
          >
            ✏️
          </button>
        )}
      </div>
    </div>
  );
}

export function SortableProductList({ items, isClientHidden, admin, onEdit, onReorder }: Props) {
  const [order, setOrder] = useState<SortableItem[]>(items);

  // Resync quand la catégorie/les données changent (nouvelle sélection, reload).
  useEffect(() => { setOrder(items); }, [items]);

  // Long-press 500ms (tolérance 5px) = active le drag. TouchSensor gère nativement
  // la coexistence scroll/drag sur iOS (avant le délai : un déplacement > 5px = scroll
  // et le drag est annulé ; immobile 500ms = drag). MouseSensor (délai court) pour le
  // desktop. Un tap court reste un tap (clic ✏️) → pas de drag intempestif.
  const sensors = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 500, tolerance: 5 } }),
    useSensor(MouseSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const onDragStart = () => {
    try { navigator.vibrate?.(50); } catch { /* no-op */ }
  };

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = order.findIndex((x) => x.product.id === active.id);
    const newIndex = order.findIndex((x) => x.product.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next); // optimistic
    onReorder(next.map((x) => x.product.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={order.map((x) => x.product.id)} strategy={verticalListSortingStrategy}>
        {order.map((item) => (
          <ProductRow key={item.product.id} item={item} isClientHidden={isClientHidden} admin={admin} onEdit={onEdit} />
        ))}
      </SortableContext>
    </DndContext>
  );
}
